import {useRef} from 'react';

import {useLazyCalculateFiscalPowerQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {ApiError, useDebouncedCallback} from 'shared/web';

import {useVehicleFormContext} from '../hooks/useVehicleFormContext';
import {Field} from './primitives/Field';

const FISCAL_POWER_CALCULATION_DEBOUNCE_MS = 300;

const isValidEngineVolume = (engineVolume: number | null): engineVolume is number =>
  engineVolume !== null && !Number.isNaN(engineVolume) && engineVolume > 0;

// Aborting an in-flight RTK Query request rejects its unwrapped promise with either a
// SerializedError ({name: 'AbortError'}) or a FetchBaseQueryError
// ({status: 'FETCH_ERROR', error: 'AbortError: ...'}). We swallow both so a superseded
// request never surfaces an error notification.
const isAbortError = (error: ApiError): boolean =>
  ('name' in error && error.name === 'AbortError') ||
  ('status' in error && error.status === 'FETCH_ERROR' && error.error.includes('AbortError'));

export function EngineVolumeField() {
  const {formApi} = useVehicleFormContext();
  const [calculateFiscalPower] = useLazyCalculateFiscalPowerQuery();
  const pendingRequest = useRef<ReturnType<typeof calculateFiscalPower>>(null);

  const debouncedCalculate = useDebouncedCallback((engineVolume: number | null) => {
    if (!isValidEngineVolume(engineVolume)) {
      return;
    }

    // Cancel any still-pending calculation so a superseded request can never win the race.
    // RTK Query owns the abort signal internally — keeping it out of the query argument
    // keeps the cache entry serializable.
    pendingRequest.current?.abort();

    const request = calculateFiscalPower({engineVolume}, true);
    pendingRequest.current = request;

    request
      .unwrap()
      .then((response) => {
        if (formApi.getValues('isFiscalPowerManualOverride') === true) {
          return;
        }
        formApi.setValue('fiscalPower', response.fiscalPower, {
          shouldDirty: true,
          shouldValidate: true,
        });
      })
      .catch((error: ApiError) => {
        if (isAbortError(error)) {
          return;
        }
        handleApiError(error);
      });
  }, FISCAL_POWER_CALCULATION_DEBOUNCE_MS);

  const handleEngineVolumeChange = (engineVolume: number | null) => {
    if (formApi.getValues('isFiscalPowerManualOverride') === true) {
      return;
    }

    debouncedCalculate(engineVolume);

    if (!isValidEngineVolume(engineVolume)) {
      pendingRequest.current?.abort();
      formApi.setValue('fiscalPower', null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  return (
    <Field
      type="number"
      name="engineVolume"
      decimalPlaces={0}
      label={i18n.t('entity.vehicle.labels.engineCapacity')}
      suffix={i18n.t('general.metric.ccm')}
      onChange={handleEngineVolumeChange}
    />
  );
}
